/**
 * The damage-number lane solver (art §8.6, ui-ux §10.2): picks where a number spawns and how far it rises so its whole
 * path avoids faces, intent roundels, the omen medallion, the scroll band and the HUD. Pure and deterministic (the x
 * jitter is seeded by the event index, so replays place numbers identically). Owner: UI/Visual (F3).
 *
 * Lanes, tried in order (the first whose path box hits no obstacle wins):
 * 1. contact — x = root + 0.25 H toward the attacker, y = 0.65 H above the feet, rise 36;
 * 2. back    — x = root − 0.30 H (away from the attacker), y = 0.55 H, rise 36;
 * 3. knee    — x = root, y = 0.30 H, rise ≤ 16 (always valid).
 * Ceiling: a rise never goes above the victim's head top − 8 px. Clamps: ≥ 8 px inside the picture interior.
 * Stacking: a new number pushes the victim's older ones up 22 px inside the ceiling; ≤ 3 alive per victim.
 * Coordinates: slot-local CSS px, y down.
 */
import type { Rect } from '../layout/fightLayout';

export interface LaneInput {
  /** Victim's root (feet contact point) in slot px. */
  rootX: number;
  feetY: number;
  /** Staging unit H in px (art §7.2; 215 at 390×844 rest). */
  H: number;
  /** +1 when the attacker stands to the victim's right, −1 to the left. */
  toward: 1 | -1;
  /** Victim's head top in slot px (the ceiling is headTop + 8). */
  headTopY: number;
  /** Picture interior (panel minus the frame band). */
  interior: Rect;
  /** Faces, intent roundels, omen, scroll band, HUD rects. */
  obstacles: Rect[];
  /** Number box size (font size × text width estimate). */
  boxW: number;
  boxH: number;
  /** Combat event index: seeds the ±8 px jitter. */
  eventIndex: number;
}

export interface LaneResult { lane: 1 | 2 | 3; x: number; y0: number; y1: number; rise: number; path: Rect }

export const LANE_RISE = 36;
export const KNEE_RISE = 16;
export const STACK_PUSH = 22;
export const MAX_ALIVE = 3;

/** Deterministic jitter in [−8, 8] from an event index (integer hash, no RNG state). */
export function laneJitter(i: number): number {
  let x = (i | 0) ^ 0x9e3779b9;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  x ^= x >>> 16;
  return ((x >>> 0) % 17) - 8;
}

const hit = (a: Rect, b: Rect): boolean => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** Solves the lane for one new number. `y0` / `y1` are the box's top at spawn / at the end of the rise. */
export function solveLane(i: LaneInput): LaneResult {
  const minX = i.interior.x + 8, maxX = i.interior.x + i.interior.w - 8 - i.boxW;
  const minY = i.interior.y + 8;
  const ceiling = Math.max(minY, i.headTopY + 8);
  const j = laneJitter(i.eventIndex);
  const lanes: Array<{ lane: 1 | 2 | 3; cx: number; cy: number; rise: number }> = [
    { lane: 1, cx: i.rootX + 0.25 * i.H * i.toward, cy: i.feetY - 0.65 * i.H, rise: LANE_RISE },
    { lane: 2, cx: i.rootX - 0.30 * i.H * i.toward, cy: i.feetY - 0.55 * i.H, rise: LANE_RISE },
    { lane: 3, cx: i.rootX, cy: i.feetY - 0.30 * i.H, rise: KNEE_RISE },
  ];
  const place = (l: typeof lanes[number]): LaneResult => {
    const x = Math.round(Math.min(Math.max(l.cx - i.boxW / 2 + j, minX), Math.max(minX, maxX)));
    const y0 = Math.round(Math.max(l.cy - i.boxH / 2, ceiling));
    const rise = Math.max(0, Math.min(l.rise, y0 - ceiling));
    const y1 = y0 - rise;
    return { lane: l.lane, x, y0, y1, rise, path: { x, y: y1, w: i.boxW, h: y0 - y1 + i.boxH } };
  };
  for (const l of lanes.slice(0, 2)) {
    const p = place(l);
    if (!i.obstacles.some((o) => hit(o, p.path))) return p;
  }
  return place(lanes[2]);
}

/** Per-victim stacks: pushes older numbers up 22 px inside the ceiling; returns ids to fade (into the ceiling, or > 3). */
export class LaneStacks {
  private readonly alive = new Map<string, Array<{ id: number; y: number }>>();

  /** Registers a new number; returns the ids that must fade (120 ms) and the shifted y of the survivors. */
  push(victim: string, id: number, y: number, ceiling: number): { fade: number[]; moved: Array<{ id: number; y: number }> } {
    const list = this.alive.get(victim) ?? [];
    const fade: number[] = [];
    const moved: Array<{ id: number; y: number }> = [];
    for (const n of list) {
      n.y -= STACK_PUSH;
      if (n.y < ceiling) fade.push(n.id); else moved.push({ id: n.id, y: n.y });
    }
    let next = list.filter((n) => !fade.includes(n.id));
    next.push({ id, y });
    while (next.length > MAX_ALIVE) { fade.push(next[0].id); next = next.slice(1); }
    this.alive.set(victim, next);
    return { fade, moved: moved.filter((m) => !fade.includes(m.id)) };
  }

  remove(victim: string, id: number): void {
    const list = this.alive.get(victim);
    if (list) this.alive.set(victim, list.filter((n) => n.id !== id));
  }

  count(victim: string): number { return this.alive.get(victim)?.length ?? 0; }
}
